import { Suspense, useEffect, useRef, useState } from "react";
import {
  //  BrowserRouter as Router,
  Route,
  Switch,
  Router,
} from "react-router-dom";
import { history } from "./redux/store/createStore";
import routes from "./config/routes";
import { ADMIN_CONSOLE_ENABLED } from "./config/envs";
import { socket } from "./service/socket";
import { useDispatch, useSelector } from "react-redux";
import { setSocketInstance } from "./redux/actions/userDetails";
import { ClipLoader } from "react-spinners";

//routes
import AuthRoute from "./components/privateRoutes/AuthRoute";
import UserRoute from "./components/privateRoutes/UserRoute";
import CustodianRoute from "./components/privateRoutes/CustodianRoute";
import JuryRoute from "./components/privateRoutes/JuryRoute";
import TribunalRoute from "./components/privateRoutes/TribunalRoute";
import { isObjectEmpty } from "./utils/utils";
import { parseUser } from "./utils/storedUser";
import NotFoundPage from "./components/HomePage/NotFound";
import { toast } from "react-toastify";
import Loading from "./components/Common/Loading";
import { lazyWithRetry } from "./utils/lazyWithRetry";

//components
const CreateOrder = lazyWithRetry(
  () => import("./components/Panel/UserDashboard/CreateOrder/CreateOrder"),
);
const OrderCreated = lazyWithRetry(
  () =>
    import("./components/Panel/UserDashboard/CreateOrder/OrderCreated/OrderCreated"),
);
const UserDepositOrderDetail = lazyWithRetry(
  () =>
    import("./components/Panel/UserDashboard/OrderDetails/DepositOrder/DepositOrderDetail"),
);
const CustodianDepositOrderDetail = lazyWithRetry(
  () =>
    import("./components/Panel/CustodianDashboard/Orders/DepositOrderDetails/DepositOrderDetails"),
);
const CustodianWithdrawOrderDetail = lazyWithRetry(
  () =>
    import("./components/Panel/CustodianDashboard/Orders/WithdrawOrderDetails/WithdrawOrderDetails"),
);
const UserWithdrawOrderDetail = lazyWithRetry(
  () =>
    import("./components/Panel/UserDashboard/OrderDetails/WithdrawOrder/WithdrawOrderDetail"),
);
const UserBankingInfo = lazyWithRetry(
  () =>
    import("./components/Authentication/UserDetail/UserInfo/BankingInfo/UserBankingInfo"),
);
const DisputedOrderDetail = lazyWithRetry(
  () => import("./components/Panel/DisputedOrder/DisputedOrder"),
);
const UserPersonalInfo = lazyWithRetry(
  () =>
    import("./components/Authentication/UserDetail/UserInfo/PersonalInfo/UserPersonalInfo"),
);
const DisputedResult = lazyWithRetry(
  () => import("./components/Panel/DisputedResult/DisputedResult"),
);
const OrderToVote = lazyWithRetry(
  () =>
    import("./components/Panel/JuryDashboard/Orders/OrderDetail/OrderDetailToVote"),
);
const AppealOrder = lazyWithRetry(
  () => import("./components/Panel/TribunalDashboard/Appeal/AppealOrder"),
);
const Settings = lazyWithRetry(
  () => import("./components/Panel/Settings/Settings"),
);
const AppealOrderDetail = lazyWithRetry(
  () => import("./components/Panel/AppealOpen/AppealOpen"),
);
const AppealOrderResult = lazyWithRetry(
  () => import("./components/Panel/AppealResult/AppealResult"),
);
const UserHome = lazyWithRetry(
  () => import("./components/Panel/UserDashboard/Dashboard/Dashboard"),
);
const UserTransaction = lazyWithRetry(
  () => import("./components/Panel/UserDashboard/Transactions/Transactions"),
);
const CustodianDashboard = lazyWithRetry(
  () =>
    import("./components/Panel/CustodianDashboard/Dashboard/CustodianDashboard"),
);
const CustodianTransaction = lazyWithRetry(
  () =>
    import("./components/Panel/CustodianDashboard/Transactions/Transactions"),
);
const JuryDashboard = lazyWithRetry(
  () => import("./components/Panel/JuryDashboard/Dashboard/JuryDashboard"),
);
const TribunalDashboard = lazyWithRetry(
  () =>
    import("./components/Panel/TribunalDashboard/Dashboard/TribunalDashboard"),
);
const TribunalTransaction = lazyWithRetry(
  () =>
    import("./components/Panel/TribunalDashboard/Transactions/Transactions"),
);
const JuryTransaction = lazyWithRetry(
  () => import("./components/Panel/JuryDashboard/Transaction/JuryTransaction"),
);
const Collateral = lazyWithRetry(
  () => import("./components/Panel/CustodianDashboard/Collateral/Collateral"),
);
const TransferFunds = lazyWithRetry(
  () => import("./components/Panel/TransferFunds/TransferFunds"),
);
const Stake = lazyWithRetry(() =>
  import("./components/Panel/JuryDashboard/Stake/Stake")
);
const ReferralPage = lazyWithRetry(() =>
  import("./components/ReferralPage/ReferralPage")
);
const REFERRALS_ENABLED = process.env.REACT_APP_REFERRALS_ENABLED === "true";

const LandingPageLazy = lazyWithRetry(() =>
  import("./components/HomePage/LandingPage")
);

const productPageLazy = lazyWithRetry(
  () => import("./components/ProductPage/ProductPage"),
);

const whyMidlPageLazy = lazyWithRetry(
  () => import("./components/WhyMidlPage/WhyMidlPage"),
);

const ecosystemPageLazy = lazyWithRetry(
  () => import("./components/EcosystemPage/EcosystemPage"),
);

const LaunchAppLazy = lazyWithRetry(
  () => import("./components/HomePage/LaunchApp/LaunchApp"),
);
const LoginLayoutLazy = lazyWithRetry(() => import("./layout/LoginLayout"));
const LoginLazy = lazyWithRetry(
  () => import("./components/Authentication/Login/Login"),
);
const RegisterLazy = lazyWithRetry(
  () => import("./components/Authentication/Register/Register"),
);
const ForgotPasswordLazy = lazyWithRetry(
  () => import("./components/Authentication/ForgotPassword/ForgotPassword"),
);
const ResetPasswordLazy = lazyWithRetry(
  () => import("./components/Authentication/ResetPassword/ResetPassword"),
);
const PhoneVerificationLazy = lazyWithRetry(
  () =>
    import("./components/Authentication/PhoneVerification/PhoneVerification"),
);
const EmailVerificationLazy = lazyWithRetry(
  () =>
    import("./components/Authentication/EmailVerification/EmailVerification"),
);
const SecurityVerificationLazy = lazyWithRetry(
  () =>
    import("./components/Authentication/SecurityVerification/SecurityVerification"),
);
const AccountCreatedLazy = lazyWithRetry(
  () => import("./components/Authentication/AccountCreated/AccountCreated"),
);
const PanelLayout = lazyWithRetry(() => import("./layout/PanelLayout"));

// DeFi surface — swap page
const SwapPage = lazyWithRetry(() => import("./components/DeFi/V4Swap/V4Swap"));

// Admin/Tribunal console
const AdminConsole = lazyWithRetry(() => import("./components/Admin/AdminConsole"));

// DeFi surface — add-liquidity / pool management
const LiquidityPage = lazyWithRetry(
  () => import("./components/DeFi/V4Liquidity/V4Liquidity"),
);

// Standalone-page wrappers under the unified Swap/Pool/My-Position chrome
const PoolsPage = lazyWithRetry(
  () => import("./components/DeFi/V4Liquidity/V4PoolPage"),
);
const PoolDetailPage = lazyWithRetry(
  () => import("./components/DeFi/V4Liquidity/V4PoolDetailPage"),
);
const PositionsPage = lazyWithRetry(
  () => import("./components/DeFi/V4Liquidity/V4MyPositionsPage"),
);
const PositionDetailPage = lazyWithRetry(
  () => import("./components/DeFi/V4Liquidity/V4PositionDetailPage"),
);

// How long a dropped socket may stay down before we tell the user anything.
// Long enough to cover a wallet deep-link round-trip / tab suspension / server
// redeploy (socket.io retries with a 1-5s backoff), short enough that a real
// outage is still reported promptly.
const RECONNECT_GRACE_MS = 8000;
// Single id so a flapping connection can never stack duplicate toasts, and so
// the warning can be dismissed the instant we reconnect.
const DISCONNECT_TOAST_ID = "socket-connection-lost";

const emitOnlineStatus = (socket, u) => {
  if (!u?._id) return;
  // The server takes the id from the token (midl-server plan §13.9 B3).
  socket.emit("changeStatusToOnline", {
    query: { mongoId: u._id.toString(), role: u.role },
    token: localStorage.getItem("Token"),
  });
};

const App = () => {
  const dispatch = useDispatch();
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOverlay, setShowOverlay] = useState(false);

  // Capture an inbound referral code (?referralCode=...) from the share link on
  // first load and persist it, so it survives the multi-step sign-up flow (the
  // registration step reads it from localStorage when submitting).
  useEffect(() => {
    if (!REFERRALS_ENABLED) return;
    const code = new URLSearchParams(window.location.search).get(
      "referralCode",
    );
    if (code && code.trim()) localStorage.setItem("referralCode", code.trim());
  }, []);

  useEffect(() => {
    if (window.location.pathname !== routes.home) {
      const handleOnline = () => {
        setIsOnline(true);
        toast.info("You are back online!");
      };
      const handleOffline = () => {
        setIsOnline(false);
        toast.error("You're offline. Check your connection.");
      };

      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);

      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      };
    }
  }, []);
  // console.log("isConnected", isConnected);

  const user =
    useSelector((state) => state?.Authentication?.user) ||
    localStorage.getItem("User");

  // Defensive parse. The old `JSON.parse(user) ? JSON.parse(user) : user` threw
  // on any non-JSON stored value, and a throw here — in the ROOT component's
  // render — unmounted the whole tree into a blank white page that survived
  // reloads. parseUser never throws and always yields an object.
  const user2 = parseUser(user);

  // `user2` is rebuilt on every render, so the socket effect below reads the
  // current user through this ref instead of taking it as a dependency.
  const userRef = useRef(user2);
  useEffect(() => {
    userRef.current = user2;
  });

  // console.log('bahir available status', user2);

  // useEffect(() => {
  //   if (isObjectEmpty(socket)) return;

  //   function onConnect() {
  //     setIsConnected(true);
  //     handleStatus();
  //     setShowOverlay(false);
  //   }

  //   function onDisconnect(reason) {
  //     setIsConnected(false);
  //     setShowOverlay(window.location.pathname !== routes.home);
  //     handleStatus();
  //   }

  //   function handleStatus() {
  //     if (user2?._id) {
  //       socket.emit("changeStatusToOnline", {
  //         query: { mongoId: user2._id.toString(), role: user2.role },
  //       });
  //     }
  //   }

  //   socket.on("connect", onConnect);
  //   socket.on("disconnect", onDisconnect);
  //   socket.on("reconnect", (attemptNumber) => {
  //     // console.log(`Reconnected to the server (attempt ${attemptNumber})`);
  //   });
  //   socket.on("error", (error) => {
  //     // console.error('Socket error:', error);
  //   });

  //   return () => {
  //     socket.off("connect", onConnect);
  //     socket.off("disconnect", onDisconnect);
  //     socket.off("reconnect");
  //     socket.off("error");
  //   };
  // }, [socket, user2]);

  // The app-wide socket is a live-update channel, not the app's lifeline: REST
  // calls and wallet/RPC traffic keep working while it is down, and socket.io
  // reconnects on its own (service/socket.js: infinite attempts, 1-5s backoff).
  // A `disconnect` therefore does NOT mean the internet is gone. The transport
  // drops routinely and recovers in well under a second — iOS suspends the page
  // while a wallet deep-link/modal owns the screen, backgrounded tabs miss
  // ping/pong, the server redeploys — so toasting on the raw event produced the
  // false "Internet Disconnected" flash (seen right next to "Successfully
  // connected to Polygon!" during wallet connect).
  // Instead: wait out a grace period and only speak up if we are STILL down
  // after it, saying what is actually wrong (device offline vs server
  // unreachable). Anything that recovers inside the grace stays silent.
  useEffect(() => {
    if (isObjectEmpty(socket)) return;

    let graceTimer = null;
    const clearGrace = () => {
      if (graceTimer) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
    };

    function onConnect() {
      clearGrace();
      setIsConnected(true);
      setShowOverlay(false);
      // Clear a warning from an earlier, longer outage the moment we are back.
      toast.dismiss(DISCONNECT_TOAST_ID);
      emitOnlineStatus(socket, userRef.current);
    }

    function onDisconnect(reason) {
      setIsConnected(false);
      // A deliberate client-side teardown (navigating away, HMR) is never a
      // connectivity problem.
      if (reason === "io client disconnect") return;
      clearGrace();
      graceTimer = setTimeout(() => {
        graceTimer = null;
        if (socket.connected) return; // recovered while we waited
        setShowOverlay(window.location.pathname !== routes.home);
        // toastId keeps a flapping connection from stacking duplicates.
        toast.error(
          navigator.onLine
            ? "Connection lost. Reconnecting…"
            : "You're offline. Please check your connection.",
          { toastId: DISCONNECT_TOAST_ID },
        );
      }, RECONNECT_GRACE_MS);
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    return () => {
      clearGrace();
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
    };
    // Deliberately depends on the socket ALONE: the effect owns a pending grace
    // timer, and re-running it on every render (as the old `user2` dep did —
    // that object is rebuilt each render) would cancel that timer and reset the
    // grace window forever. The current user is read through userRef instead.
  }, [socket]);

  // Announce presence when the identity becomes known (or changes) on an
  // already-connected socket; the connect handler above covers the other order.
  useEffect(() => {
    if (isObjectEmpty(socket) || !socket.connected) return;
    emitOnlineStatus(socket, user2);
  }, [socket, user2?._id, user2?.role]);

  useEffect(() => {
    // console.log("setting socket", socket);
    if (user) {
      dispatch(setSocketInstance(socket));
    }
  }, [socket, user]);

  return (
    <>
      {/* <h2>Result: {result}</h2> */}
      {showOverlay && (
        <div className="loaderWrapper">
          <ClipLoader className="login-clip-loader" color={"blue"} size={60} />
          <p
            style={{
              marginTop: "20px",
              color: "blue",
              fontSize: "18px",
            }}
          >
            Attempting to reconnect....
          </p>
        </div>
      )}
      <Router history={history}>
        <Suspense
          fallback={
            <div
              style={{
                display: "flex",
                height: "100vh",
              }}
            >
              <span
                style={{
                  margin: "auto",
                }}
              >
                <ClipLoader color="#044DB5" size={60} />
              </span>
            </div>
          }
        >
          <Switch>
            <Route exact path={routes.home} component={LandingPageLazy} />
            <Route
              exact
              path={routes.productPage}
              component={productPageLazy}
            />
            <Route
              exact
              path={routes.whyMidlPage}
              component={whyMidlPageLazy}
            />
            <Route
              exact
              path={routes.ecosystemPage}
              component={ecosystemPageLazy}
            />
            <Route exact path={`${routes.launchApp}/:userRole`}>
              <LoginLayoutLazy>
                <LaunchAppLazy />
              </LoginLayoutLazy>
            </Route>

            {/*Authentication*/}
            <Route exact path={routes.auth.logIn} component={LoginLazy} />
            <Route exact path={routes.auth.register} component={RegisterLazy} />
            <Route
              exact
              path={routes.auth.forgotPassword}
              component={ForgotPasswordLazy}
            />
            <Route
              exact
              path={routes.auth.resetPassword}
              component={ResetPasswordLazy}
            />
            <Route
              exact
              path={routes.auth.phoneVerification}
              component={PhoneVerificationLazy}
            />
            <Route
              exact
              path={routes.auth.emailVerification}
              component={EmailVerificationLazy}
            />
            <Route
              exact
              path={routes.auth.securityVerification}
              component={SecurityVerificationLazy}
            />
            <Route
              exact
              path={routes.auth.accountCreated}
              component={AccountCreatedLazy}
            />

            {/*User onboarding*/}
            <AuthRoute path={routes.user.normalUserPersonalInfoForm}>
              <UserPersonalInfo />
            </AuthRoute>
            <AuthRoute path={routes.user.normalUserBankingInfoForm}>
              <UserBankingInfo />
            </AuthRoute>

            {/* User Panel */}
            <UserRoute path={routes.panel.user.createOrder}>
              <PanelLayout>
                <CreateOrder />
              </PanelLayout>
            </UserRoute>
            <UserRoute path={routes.panel.user.home}>
              <PanelLayout>
                <UserHome />
              </PanelLayout>
            </UserRoute>
            <UserRoute path={routes.panel.user.orderCreated}>
              <PanelLayout>
                <OrderCreated />
              </PanelLayout>
            </UserRoute>
            <UserRoute path={routes.panel.user.depositOrder}>
              <PanelLayout>
                <UserDepositOrderDetail />
              </PanelLayout>
            </UserRoute>
            <UserRoute path={routes.panel.user.withdrawOrder}>
              <PanelLayout>
                <UserWithdrawOrderDetail />
              </PanelLayout>
            </UserRoute>
            <UserRoute path={routes.panel.user.disputedOrder}>
              <PanelLayout>
                <DisputedOrderDetail />
              </PanelLayout>
            </UserRoute>
            <UserRoute path={routes.panel.user.disputedResult}>
              <PanelLayout>
                <DisputedResult />
              </PanelLayout>
            </UserRoute>
            <UserRoute path={routes.panel.user.appealedOrder}>
              <PanelLayout>
                <AppealOrderDetail />
              </PanelLayout>
            </UserRoute>
            <UserRoute path={routes.panel.user.appealedResult}>
              <PanelLayout>
                <AppealOrderResult />
              </PanelLayout>
            </UserRoute>
            <UserRoute path={routes.panel.user.settings}>
              <PanelLayout>
                <Settings />
              </PanelLayout>
            </UserRoute>
            <UserRoute path={routes.panel.user.transactions}>
              <PanelLayout>
                <UserTransaction />
              </PanelLayout>
            </UserRoute>
            {REFERRALS_ENABLED && (
              <UserRoute path={routes.panel.user.referral}>
                <PanelLayout>
                  <ReferralPage />
                </PanelLayout>
              </UserRoute>
            )}

            {/*Added a dummy route to set jury/tribunal voting screen */}

            {/*user/jury */}
            <UserRoute path={routes.panel.user.dummyRoute}>
              <PanelLayout>
                <OrderToVote />
              </PanelLayout>
            </UserRoute>

            {/*user/tribunal */}
            <UserRoute path={routes.panel.user.appealDummyRoute}>
              <PanelLayout>
                <AppealOrder />
              </PanelLayout>
            </UserRoute>

            <UserRoute path={routes.panel.user.tribunalDashboard}>
              <PanelLayout>
                <TribunalDashboard />
              </PanelLayout>
            </UserRoute>

            {/*Added a dummy route to set jury voting screen */}

            {/*Custodian Panel */}
            <CustodianRoute path={routes.panel.custodian.dashboard}>
              <PanelLayout>
                <CustodianDashboard />
              </PanelLayout>
            </CustodianRoute>
            <CustodianRoute path={routes.panel.custodian.depositOrder}>
              <PanelLayout>
                <CustodianDepositOrderDetail />
              </PanelLayout>
            </CustodianRoute>
            <CustodianRoute path={routes.panel.custodian.withdrawOrder}>
              <PanelLayout>
                <CustodianWithdrawOrderDetail />
              </PanelLayout>
            </CustodianRoute>
            <CustodianRoute path={routes.panel.custodian.disputedOrder}>
              <PanelLayout>
                <DisputedOrderDetail />
              </PanelLayout>
            </CustodianRoute>
            <CustodianRoute path={routes.panel.custodian.disputedResult}>
              <PanelLayout>
                <DisputedResult />
              </PanelLayout>
            </CustodianRoute>
            <CustodianRoute path={routes.panel.custodian.appealedOrder}>
              <PanelLayout>
                <AppealOrderDetail />
              </PanelLayout>
            </CustodianRoute>
            <CustodianRoute path={routes.panel.custodian.appealedResult}>
              <PanelLayout>
                <AppealOrderResult />
              </PanelLayout>
            </CustodianRoute>
            <CustodianRoute path={routes.panel.custodian.settings}>
              <PanelLayout>
                <Settings />
              </PanelLayout>
            </CustodianRoute>
            <CustodianRoute path={routes.panel.custodian.transactions}>
              <PanelLayout>
                <CustodianTransaction />
              </PanelLayout>
            </CustodianRoute>
            <CustodianRoute path={routes.panel.custodian.collateral}>
              <PanelLayout>
                <Collateral />
              </PanelLayout>
            </CustodianRoute>
            {REFERRALS_ENABLED && (
              <CustodianRoute path={routes.panel.custodian.referral}>
                <PanelLayout>
                  <ReferralPage />
                </PanelLayout>
              </CustodianRoute>
            )}

            {/*Jury Panel */}
            <JuryRoute path={routes.panel.jury.orderToVote}>
              <PanelLayout>
                <OrderToVote />
              </PanelLayout>
            </JuryRoute>
            <JuryRoute path={routes.panel.jury.dashboard}>
              <PanelLayout>
                <JuryDashboard />
              </PanelLayout>
            </JuryRoute>
            <JuryRoute path={routes.panel.jury.transactions}>
              <PanelLayout>
                <JuryTransaction />
              </PanelLayout>
            </JuryRoute>
            <JuryRoute path={routes.panel.jury.settings}>
              <PanelLayout>
                <Settings />
              </PanelLayout>
            </JuryRoute>
            <JuryRoute path={routes.panel.jury.stakeNow}>
              <PanelLayout>
                <Stake />
              </PanelLayout>
            </JuryRoute>
            {REFERRALS_ENABLED && (
              <JuryRoute path={routes.panel.jury.referral}>
                <PanelLayout>
                  <ReferralPage />
                </PanelLayout>
              </JuryRoute>
            )}

            {/*Tribunal Panel */}
            <TribunalRoute path={routes.panel.tribunal.appealOrder}>
              <PanelLayout>
                <AppealOrder />
              </PanelLayout>
            </TribunalRoute>
            <TribunalRoute path={routes.panel.tribunal.dashboard}>
              <PanelLayout>
                <TribunalDashboard />
              </PanelLayout>
            </TribunalRoute>
            <TribunalRoute path={routes.panel.tribunal.transactions}>
              <PanelLayout>
                <TribunalTransaction />
              </PanelLayout>
            </TribunalRoute>
            <TribunalRoute path={routes.panel.tribunal.settings}>
              <PanelLayout>
                <Settings />
              </PanelLayout>
            </TribunalRoute>
            {REFERRALS_ENABLED && (
              <TribunalRoute path={routes.panel.tribunal.referral}>
                <PanelLayout>
                  <ReferralPage />
                </PanelLayout>
              </TribunalRoute>
            )}

            {/* swap page (behind auth) */}
            <AuthRoute exact path={routes.defi.swap}>
              <PanelLayout>
                <SwapPage />
              </PanelLayout>
            </AuthRoute>

            {/* add-liquidity / pool management (behind auth) */}
            <AuthRoute exact path={routes.defi.liquidity}>
              <PanelLayout>
                <LiquidityPage />
              </PanelLayout>
            </AuthRoute>

            {/* unified Pool list page */}
            <AuthRoute exact path={routes.defi.pools}>
              <PanelLayout>
                <PoolsPage />
              </PanelLayout>
            </AuthRoute>

            {/* per-pool detail page (Uniswap-style) */}
            <AuthRoute exact path={routes.defi.poolDetail}>
              <PanelLayout>
                <PoolDetailPage />
              </PanelLayout>
            </AuthRoute>

            {/* unified My Positions page */}
            <AuthRoute exact path={routes.defi.positions}>
              <PanelLayout>
                <PositionsPage />
              </PanelLayout>
            </AuthRoute>

            {/* per-NFT position detail */}
            <AuthRoute exact path={routes.defi.position}>
              <PanelLayout>
                <PositionDetailPage />
              </PanelLayout>
            </AuthRoute>

            <AuthRoute exact path={routes.panel.transferFunds}>
              <PanelLayout>
                <TransferFunds />
              </PanelLayout>
            </AuthRoute>

            {/* Admin / Tribunal console (tribunal === admin — same guard).
                Mounted in every environment, production included (see
                config/envs.js). If this flag is ever turned back off, the
                tribunal login redirect falls back to the Tribunal dashboard
                instead of pushing an unmounted /admin. */}
            {ADMIN_CONSOLE_ENABLED && (
              <TribunalRoute exact path={routes.admin.root}>
                <PanelLayout>
                  <AdminConsole />
                </PanelLayout>
              </TribunalRoute>
            )}

            <Route component={NotFoundPage} />
          </Switch>
        </Suspense>
      </Router>
    </>
  );
};

export default App;
